<template>
    <div ref="list" class="product-list">
        <ul
            v-if="products && products.length"
            class="row"
            :class="{
                'product-list__grid': viewMode === 'grid',
                'product-list__list': viewMode === 'list'
            }"
        >
            <li
                v-for="product in productList"
                :key="product.id"
                :data-gtm-product-id="`catalog-${product.sku}`"
                class="product-list__item col-xs-6 col-sm-6 col-md-3"
                data-gtm-element="product-item"
            >
                <ProductItem
                    v-bind="product"
                    :add-to-wishlist-parent="addToWishList"
                    :remove-from-wishlist-parent="removeFromWishList"
                />
            </li>
        </ul>
        <div v-else>
            <div
                v-if="noProductsBlock"
                class="category-list"
                v-html="noProductsBlock"
            ></div>
            <slot />
        </div>
        <div v-if="maxPriceInfo" class="max-price" v-html="maxPriceInfo" />
    </div>
</template>

<script>
import { omit } from 'lodash'
import { mapState, mapGetters } from 'vuex'
import { initRteTooltips } from '@vue/UI/BsTooltip'
import ProductItem from '../ProductItem'
import { getTooltipDataFromCms } from '../../../../js/utils/getCmsBlocks'
import handleSliderArrows from '@js/utils/handleSliderArrows.js'
import axios from 'axios'

const PRODUCTS_UPDATE_EVENT =
    typeof Event !== 'undefined' ? new Event('apteline:products-update') : null

export default {
    name: 'ProductList',
    components: {
        ProductItem
    },
    mixins: [initRteTooltips],
    props: {
        products: {
            type: Array,
            default: () => []
        },
        isSearchPage: {
            type: Boolean,
            default: false
        },
        viewMode: {
            type: String,
            default: 'grid'
        }
    },
    data() {
        return {
            blazy: null,
            lazyLoader: null,
            whishlist: []
        }
    },
    computed: {
        ...mapState(['noProductsBlock']),
        ...mapState({
            category: ({ options }) => options?.category,
            currentPage: ({ params }) => Number(params.p),
            pagination: ({ pagination }) => pagination,
            limit: ({ params }) => Number(params.resultsPP),
            placeholderImage: ({ options }) =>
                options?.category?.placeholderImage
        }),
        ...mapGetters(['maxPriceInfo']),
        productList() {
            return [...this.products].map((productItem, index) => {
                const {
                    document_data: { ...product }
                } = productItem
                const productOfTheWeek = product.dataProductOfTheWeek
                    ? '[Produkt tygodnia]'
                    : ''
                product.isRx = !!product.isRx
                product.isRefundable = product.isRefundable === 'Tak'
                product.isAvailable = product.in_stock
                product.isAllowedAtPharmacyPoint =
                    product.is_allowed_at_pharmacy_point
                product.hasUpsell = product.has_product_upsells
                product.labels = product.attr_product_labels
                product.gaListValue = this.isSearchPage
                    ? 'Catalog Page - Wyniki wyszukiwania null'
                    : `Catalog Page - ${this.category.title} ${this.category.id}${productOfTheWeek}`

                product.viewMode = this.viewMode
                product.index = index
                product.page = this.currentPage
                product.wishList = this.whishlist.includes(parseInt(product.id))

                return product
            })
        },
        productListImpression() {
            const impression = {}
            this.productList.forEach((product, listPosition) => {
                const position =
                    (this.currentPage > 1
                        ? this.limit * (this.currentPage - 1)
                        : 0) +
                    listPosition +
                    1
                const productOfTheWeek = product.dataProductOfTheWeek
                    ? '[Produkt tygodnia]'
                    : ''
                impression[`catalog-${product.sku}`] = {
                    brand: product.brand || '',
                    category: product.category || this.category.id,
                    id: product.sku,
                    list: `Catalog Page - ${this.category.title} ${this.category.id}${productOfTheWeek}`,
                    listPosition,
                    name: product.name,
                    position,
                    variant: product.variant
                }
            })
            return impression
        }
    },
    watch: {
        productList: {
            handler() {
                if (
                    typeof window !== 'undefined' &&
                    window.history &&
                    window.history.scrollRestoration
                ) {
                    window.history.scrollRestoration = 'manual'
                }
                this.reloadUpsellModule()
                this.updateProductListEvent()
                this.updateCmsBlocks()

                this.$nextTick(() => {
                    this.initImages()
                })
            },
            immediate: true
        },
        productListImpression(newList, oldList) {
            this.staticImpressionsValidate(newList, oldList)
        },

        currentPage() {
            this.$nextTick(() => {
                this.initImages()
            })
        }
    },
    mounted() {
        this.updateCmsBlocks()
        this.staticImpressionsValidate(this.productListImpression)

        const unwatch = this.$watch(
            'productList',
            function () {
                this.reloadProductOutModule()
                window.dispatchEvent(
                    new CustomEvent('reloadVueModule', {
                        detail: {
                            moduleName: 'add-to-cart'
                        }
                    })
                )
                if (unwatch) {
                    unwatch()
                }
            },
            { immediate: true }
        )

        if (this.noProductsBlock) {
            this.$nextTick(() => {
                this.initSlider()
            })
        }

        axios
            .get(
                BASE_URL +
                    'aoestatic/call/index?getBlocks[whishlist]=whishlist&isAjax=1',
                {}
            )
            .then((response) => {
                if (response.status == 200) {
                    this.whishlist = JSON.parse(response.data.blocks.whishlist)
                    this.productList.forEach((product) => {
                        product.wishList = this.whishlist.includes(
                            parseInt(product.id)
                        )
                    })
                }
            })
    },
    methods: {
        staticImpressionsValidate(newList, oldList) {
            let staticImpressions = window.staticImpressions || []
            if (staticImpressions && oldList) {
                staticImpressions = omit(
                    staticImpressions,
                    Object.keys(oldList)
                )
            }
            window.staticImpressions = Object.assign(staticImpressions, newList)
        },
        reloadUpsellModule() {
            if (typeof CustomEvent === 'undefined') {
                return
            }

            this.$nextTick(() => {
                window.dispatchEvent(
                    new CustomEvent('reloadVueModule', {
                        detail: {
                            moduleName: 'upsell-modal'
                        }
                    })
                )
            })
        },
        reloadProductOutModule() {
            if (typeof CustomEvent === 'undefined') {
                return
            }

            this.$nextTick(() => {
                window.dispatchEvent(
                    new CustomEvent('reloadVueModule', {
                        detail: {
                            moduleName: 'product-out'
                        }
                    })
                )
            })
        },
        updateProductListEvent() {
            this.$nextTick(() => {
                typeof document !== 'undefined' &&
                    document.dispatchEvent(PRODUCTS_UPDATE_EVENT)
            })
        },
        updateCmsBlocks() {
            this.$nextTick(() => {
                Array.from(
                    this.$refs.list.querySelectorAll('[data-tooltip-cms]')
                ).forEach((element) => {
                    if (element.Tooltip) {
                        element.Tooltip.dispose()
                    }
                })

                getTooltipDataFromCms(
                    Array.from(
                        this.$refs.list.querySelectorAll('[data-tooltip-cms]')
                    )
                )
            })
        },
        initImages() {
            try {
                // this.resetLazyImages()

                if (this.lazyLoader) {
                    try {
                        this.lazyLoader.destroy()
                    } catch (destroyError) {
                        console.error('LazyLoad destroy error:', destroyError)
                    }
                }

                this.lazyLoader = new LazyLoad({
                    elements_selector: '.product-lazy',
                    threshold: 300,
                    callback_error: (element) => {
                        console.error('LazyLoad error:', {
                            element: element,
                            src:
                                element.getAttribute('data-src') ||
                                element.getAttribute('src'),
                            class: element.className,
                            parent: element.parentNode
                                ? element.parentNode.className
                                : 'no-parent'
                        })
                        element.src = '/images/fallback.jpg'
                        element.classList.add('has-fallback')
                    },
                    callback_loaded: (element) => {
                        element.classList.remove('product-lazy')
                        // console.log(
                        //     'LazyLoad success:',
                        //     element.getAttribute('data-src') ||
                        //         element.getAttribute('src')
                        // )
                    }
                })

                setTimeout(() => {
                    if (this.lazyLoader) {
                        try {
                            this.lazyLoader.update()
                            console.log('LazyLoader updated after timeout')
                        } catch (updateError) {
                            console.error('LazyLoad update error:', updateError)
                        }
                    }
                }, 500)
            } catch (error) {
                console.error('LazyLoad initialization error:', error)
                try {
                    document
                        .querySelectorAll('.product-lazy')
                        .forEach((img) => {
                            if (img.getAttribute('data-src')) {
                                console.log(
                                    'Fallback loading for:',
                                    img.getAttribute('data-src')
                                )
                                img.src = img.getAttribute('data-src')
                                img.removeAttribute('data-src')
                                img.classList.remove('product-lazy')
                            }
                        })
                } catch (fallbackError) {
                    console.error(
                        'Even fallback loading failed:',
                        fallbackError
                    )
                }
            }
        },
        resetLazyImages() {
            document.querySelectorAll('.product-lazy').forEach((img) => {
                img.classList.remove('entered', 'error', 'has-fallback')
                img.removeAttribute('data-ll-status')
                if (img.getAttribute('data-src')) {
                    img.src = ''
                }
            })
        },
        initSlider() {
            const blazyProductSlider = new LazyLoad({
                selector: '.product-slider .b-lazy',
                threshold: 300
            })
            const sliders = document.querySelectorAll('.product-slider')
            sliders.forEach((el) => {
                const slider = el.querySelector('.slider--row')
                this.slider = new window.Glider(slider, {
                    slidesToShow: 1,
                    slidesToScroll: 1,
                    scrollLock: true,
                    rewind: true,
                    draggable: false,
                    arrows: {
                        prev: el.querySelector('.product-slider__btn--left'),
                        next: el.querySelector('.product-slider__btn--right')
                    },
                    responsive: [
                        {
                            breakpoint: 360,
                            settings: {
                                slidesToShow: 1.5
                            }
                        },
                        {
                            breakpoint: 768,
                            settings: {
                                slidesToShow: 2
                            }
                        },
                        {
                            breakpoint: 992,
                            settings: {
                                slidesToShow: 3
                            }
                        }
                    ]
                })
                handleSliderArrows(this.slider)

                slider.addEventListener('glider-slide-visible', () =>
                    blazyProductSlider.revalidate()
                )

                slider.addEventListener('glider-loaded', function () {
                    handleSliderArrows(this.slider)
                })

                if (window?.GoogleAnalyticsUniversal) {
                    const products = el.querySelectorAll('.product-list__item')
                    products.forEach((product, index) => {
                        const name = product.dataset.gtmProductId
                        const item = {
                            brand: product.dataset.brand,
                            category: product.dataset.category,
                            id: product.dataset.sku,
                            list: 'Brak produktów - ' + product.dataset.title,
                            listPosition: index,
                            name: product.dataset.name,
                            position: index,
                            variant: product.dataset.variant
                        }
                        window.staticImpressions[name] = item
                    })
                }
            })
            window.Aoe_Static.replaceAjaxBlocks()
        },
        addToWishList(productId) {
            if (this.whishlist.indexOf(productId) === -1) {
                this.whishlist.push(productId)
            }
        },
        removeFromWishList(productId) {
            const index = this.whishlist.indexOf(productId)
            if (index >= 0) {
                this.whishlist.splice(index, 1)
            }
        }
    }
}
</script>

<style lang="scss" scoped>
.product-list {
    &__item {
        margin-bottom: 20px;
        width: 290px;
        display: flex;
        flex-wrap: wrap;
        padding-left: 10px;
        padding-right: 10px;

        @media #{$media-to-md} {
            width: 50%;
        }

        @media #{$media-xs} {
            width: 33%;
        }

        @media screen and (max-width: 560px) {
            width: 50%;
        }

        @media screen and (max-width: 368px) {
            min-width: 50%;
            width: 50%;
        }

        &__item {
            flex: 0 1 calc(33.33% - 20px);

            @media #{$media-to-md} {
                flex: 0 1 calc(50% - 20px);
            }

            @media #{$media-xs} {
                flex: 0 1 calc(33.33% - 20px);
            }

            @media screen and (max-width: 560px) {
                flex: 0 1 calc(50% - 20px);
            }

            @media screen and (max-width: 368px) {
                flex: 0 1 calc(100% - 20px);
            }
        }
    }
}

.max-price {
    margin-bottom: 3rem;
}

.category-list {
    .h1 {
        font-size: 32px;
        text-align: center;
        position: relative;
        text-transform: none;
    }
}
</style>
